import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { ConsensusOkoAssistant } from "./ConsensusOkoAssistant";
import { ConsensusOkoLogo } from "./ConsensusOkoLogo";
import { SoloTeamTimeCard } from "./SoloTeamTimeCard";
import { colors, constants } from "utils";
import LightbulbIcon from "assets/Lightbulb.svg";
import styled from "styled-components";

export const ConsensusLeftPanel = ({ mode, questionNumber, teamRound, talkMoves, timer, timerValue, explanation, collaborationTip, prepareToShare, speechPhrase, isSpeechPlaying }) => {
  const [showHint, setShowHint] = useState(false);
  const [bubbleTop, setBubbleTop] = useState(null);

  useEffect(() => {
    setShowHint(false);
  }, [mode, explanation]);

  const toggleHint = () => {
    setShowHint(prev => !prev);
  };

  const hintButtonText = showHint ? "Hide Hint" : "See Hint";

  return (
    <Container>
      <ConsensusOkoLogo color={mode === constants.SOLO_TIME ? colors.blue500 : colors.pink800} />
      {questionNumber > 0 && (
        <QuestionPill $color={mode === constants.SOLO_TIME ? colors.blue800 : colors.pink800}>
          Question {questionNumber}
        </QuestionPill>
      )}
      <SoloCardContent >
        <SoloTeamTimeCard mode={mode} teamRound={teamRound} talkMoves={talkMoves} timer={timer} timerValue={timerValue} explanation={explanation} showHint={showHint} collaborationTip={collaborationTip} prepareToShare={prepareToShare} bubbleTop={bubbleTop} />
      </SoloCardContent>
      <div>
        <ConsensusOkoAssistant phrase={speechPhrase} isPlayingAudio={isSpeechPlaying} onBubbleTop={setBubbleTop} />
      </div>
      {explanation && <HintButtonContent>
        <HintButton
          onClick={toggleHint}
          type="submit">
          <HintContainer>
            <img src={LightbulbIcon} alt="lightbulb" />
            {hintButtonText}
          </HintContainer>
        </HintButton>
      </HintButtonContent>}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: fixed;
  /* dvh keeps the bottom-anchored hint/bot above mobile browser chrome. */
  height: 100vh;
  height: 100dvh;
  left: 0;
  top: 0;
  width: 360px;
`

// 32px right of the 184px logo (left 24px), centred on its 56px height.
const QuestionPill = styled.div`
  position: absolute;
  left: 240px;
  top: 36px;
  padding: 4px 8px;
  border: 2px solid ${({ $color }) => $color};
  border-radius: 9999px;
  background: white;
  color: ${({ $color }) => $color};
  font: 700 16px/22px Inter, sans-serif;
  letter-spacing: 0.25px;
  white-space: nowrap;
`

const SoloCardContent = styled.div`
  display: flex;
  position: fixed;
  align-items: center;
  flex-direction: column;
  flex-grow: 1;
  margin-left: 24px;
  top: 116px;

  /* Tablet: the card fills the height left over above the bot and a
     typical bubble (the reserve), but never drops below 400px — on short
     viewports the card then extends into the reserve and the bubble
     overlays its bottom rather than squeezing it further; the content
     scrolls inside the card. */
  @media ${constants.TABLET_MEDIA_QUERY} {
    /* 247px (not 300) gives the card ~15% more height on a 768px-tall
       iPad; a tall bubble overlays the card bottom as before. */
    bottom: 247px;
    min-height: 400px;
  }
`

const HintButton = styled(Button)`
  background-color: white;
  border: 1px solid ${colors.pink800};
  border-radius: 100px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px 24px;
  width: 132px;
  height: 36px;

  /* pink800 text on pink50 is 5.65:1, above the 4.5:1 AA floor. Tripled to
     outrank Bootstrap's ":not(.btn-check) + .btn:active", which otherwise
     paints the pressed state primary blue. */
  &&&:hover,
  &&&:focus,
  &&&:active {
    background-color: ${colors.pink50};
    border-color: ${colors.pink800};
  }
`

const HintContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  color: ${colors.pink800};
  font-size: 14px;
  font-weight: 500;
`

// Sits to the right of the bot: 24px inset + 56px volume gutter + 122px bot
// (desktop) ends at 202px, so 210px clears it at every width.
const HintButtonContent = styled.div`
  position: absolute;
  bottom: 32px;
  left: 210px;
`
